import { Avatar, useMantineTheme } from '@mantine/core';
import { space } from '@repo/theme/primitives';
import { pickColorType } from '../../utils/color';
import { AIAvatar } from '../AI/Avatar';
import type { AvatarUser } from './common';
import { TooltipAvatar } from './common';
import { colorGroupToFillMap, getUserInitialsFromName } from './helpers';

type UserAvatarSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg';

const userAvatarSize: Record<UserAvatarSize, number> = {
	xxs: space[4],
	xs: space[5],
	sm: space[6],
	md: space[7],
	lg: space[8],
} as const;

interface UserAvatarProps {
	size: number | UserAvatarSize;
	user: AvatarUser;
	tooltip?: string;
	enableLink?: boolean;
}

export function UserAvatar({
	user,
	tooltip,
	size,
	enableLink = false,
}: UserAvatarProps) {
	const theme = useMantineTheme();

	const { first_name, last_name, display_name, disabled, profile_picture } =
		user;

	// Default xs is 16
	const convertedSize =
		typeof size === 'string'
			? (userAvatarSize[size as keyof typeof userAvatarSize] ?? 16)
			: size;
	if (`${first_name} ${last_name}` === 'Secoda AI') {
		return <AIAvatar size={convertedSize} speed="slow" iconSpeed="none" />;
	}

	const colorGroup = pickColorType(display_name, disabled);
	const { fillStart, fillEnd, textColor } = colorGroupToFillMap(colorGroup);
	const initials = getUserInitialsFromName(display_name) || '';

	return (
		<Avatar
			variant="filled"
			size={convertedSize}
			h={convertedSize}
			w={convertedSize}
			mih={convertedSize}
			miw={convertedSize}
			sx={{
				backgroundImage: theme.fn.gradient({
					from: fillStart,
					to: fillEnd,
					deg: 180,
				}),
				color: textColor,
			}}
			src={profile_picture}
			alt={display_name}
			component={TooltipAvatar}
			user={user}
			tooltip={tooltip}
			enableLink={enableLink}
		>
			<div style={{ fontSize: convertedSize / 2 }}>{initials}</div>
		</Avatar>
	);
}
